<template>
    <div>
        <!-- DESKTOP: Apenas banners tipo 'web' -->
        <div v-if="webBanners.length > 0"
            class="hidden lg:block relative w-full max-w-[981px] h-[158px] mx-auto rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            @mouseenter="pauseAutoplay" @mouseleave="startAutoplay">
            <transition name="fade" mode="out-in">
                <div :key="currentWebIndex" class="w-full h-full">
                    <a v-if="currentWebBanner.link" :href="currentWebBanner.link" target="_blank"
                        rel="noopener noreferrer" class="block w-full h-full">
                        <img :src="`/storage/${currentWebBanner.arquivos[0].caminho}`"
                            :alt="currentWebBanner.title || 'Banner'" class="w-full h-full object-cover"
                            @error="handleImageError" />
                    </a>
                    <img v-else :src="`/storage/${currentWebBanner.arquivos[0].caminho}`"
                        :alt="currentWebBanner.title || 'Banner'" class="w-full h-full object-cover"
                        @error="handleImageError" />
                </div>
            </transition>
        </div>

        <!-- MOBILE: Apenas banners tipo 'mobile' -->
        <div v-if="mobileBanners.length > 0"
            class="lg:hidden relative w-full max-w-[981px] h-[90px] mx-auto rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            @mouseenter="pauseAutoplay" @mouseleave="startAutoplay">
            <transition name="fade" mode="out-in">
                <div :key="currentMobileIndex" class="w-full h-full">
                    <a v-if="currentMobileBanner.link" :href="currentMobileBanner.link" target="_blank"
                        rel="noopener noreferrer" class="block w-full h-full">
                        <img :src="`/storage/${currentMobileBanner.arquivos[0].caminho}`"
                            :alt="currentMobileBanner.title || 'Banner'" class="w-full h-full object-cover"
                            @error="handleImageError" />
                    </a>
                    <img v-else :src="`/storage/${currentMobileBanner.arquivos[0].caminho}`"
                        :alt="currentMobileBanner.title || 'Banner'" class="w-full h-full object-cover"
                        @error="handleImageError" />
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
    banners: {
        type: Array,
        required: true,
        default: () => []
    },
    interval: {
        type: Number,
        default: 5000
    }
})

// Índices separados para web e mobile
const currentWebIndex = ref(0)
const currentMobileIndex = ref(0)

let webAutoplayInterval = null
let mobileAutoplayInterval = null

// Computed para separar banners por tipo
const webBanners = computed(() => {
    return props.banners.filter(banner => banner.tipo === 'web')
})

const mobileBanners = computed(() => {
    return props.banners.filter(banner => banner.tipo === 'mobile')
})

// Banners atuais
const currentWebBanner = computed(() => {
    return webBanners.value[currentWebIndex.value] || {}
})

const currentMobileBanner = computed(() => {
    return mobileBanners.value[currentMobileIndex.value] || {}
})

// Controles do carousel WEB
const nextWebSlide = () => {
    if (webBanners.value.length <= 1) return
    currentWebIndex.value = (currentWebIndex.value + 1) % webBanners.value.length
}

const startWebAutoplay = () => {
    if (webBanners.value.length <= 1) return
    pauseWebAutoplay()
    webAutoplayInterval = setInterval(nextWebSlide, props.interval)
}

const pauseWebAutoplay = () => {
    if (webAutoplayInterval) {
        clearInterval(webAutoplayInterval)
        webAutoplayInterval = null
    }
}

// Controles do carousel MOBILE
const nextMobileSlide = () => {
    if (mobileBanners.value.length <= 1) return
    currentMobileIndex.value = (currentMobileIndex.value + 1) % mobileBanners.value.length
}

const startMobileAutoplay = () => {
    if (mobileBanners.value.length <= 1) return
    pauseMobileAutoplay()
    mobileAutoplayInterval = setInterval(nextMobileSlide, props.interval)
}

const pauseMobileAutoplay = () => {
    if (mobileAutoplayInterval) {
        clearInterval(mobileAutoplayInterval)
        mobileAutoplayInterval = null
    }
}

// Métodos unificados para o template
const pauseAutoplay = () => {
    pauseWebAutoplay()
    pauseMobileAutoplay()
}

const startAutoplay = () => {
    startWebAutoplay()
    startMobileAutoplay()
}

// Watch para resetar quando banners mudarem
watch(
    () => props.banners,
    () => {
        currentWebIndex.value = 0
        currentMobileIndex.value = 0
        pauseAutoplay()
        startAutoplay()
    },
    { deep: true }
)

onMounted(() => {
    startAutoplay()
})

onUnmounted(() => {
    pauseAutoplay()
})

const handleImageError = (e) => {
    if (e.target.src !== '/images/placeholder.svg') {
        e.target.src = '/images/placeholder.svg'
    }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.6s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
